/**
 * Manage Client Page — View and edit client details
 */

import { useState, useEffect, useRef, FormEvent } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, updateDoc, collection, getDocs, query, orderBy, limit, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, IS_REHEARSAL } from '../../services/firebase';
import { useImpersonation } from '../../hooks/useImpersonation';
import { useAuth } from '../../hooks/useAuth';
import { ExportEmailModal } from '../Dashboard/ExportEmailModal';
import { PortalHeader } from '../Layout/PortalHeader';
import { ClientDiscountCard } from './ClientDiscountCard';
import { ClientPromoCodesCard } from './ClientPromoCodesCard';

/**
 * Live status text for the Send Cool-Off card. Owns its own 1-second tick
 * keyed on the value (same shape as the Dashboard's CooldownTimer) so it never
 * shows a stale closure value. `onExpired` fires once when the countdown
 * crosses zero so the parent can drop its copy and disable the button.
 */
function CooldownRemaining({ cooldownUntil, onExpired }: { cooldownUntil: number; onExpired?: () => void }) {
  const [text, setText] = useState('');
  const [active, setActive] = useState(false);
  // Latest callback without restarting the interval; updated in an effect,
  // never in the render body (same pattern as the Dashboard's CooldownTimer).
  const onExpiredRef = useRef(onExpired);
  useEffect(() => { onExpiredRef.current = onExpired; });
  useEffect(() => {
    let wasActive = cooldownUntil > Date.now();
    const tick = () => {
      const diff = cooldownUntil - Date.now();
      if (diff <= 0) {
        setText('Not active');
        setActive(false);
        if (wasActive) {
          wasActive = false;
          onExpiredRef.current?.();
        }
        return;
      }
      const min = Math.floor(diff / 60000);
      const sec = Math.floor((diff % 60000) / 1000);
      setText(`Active — ${min}:${String(sec).padStart(2, '0')} remaining`);
      setActive(true);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [cooldownUntil]);
  return <strong style={{ color: active ? '#5eead4' : 'var(--gray-400)' }}>{text}</strong>;
}

function formatWhen(ms: number): string {
  return new Date(ms).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

interface CooldownOverrideInfo {
  at: Date | null;
  by: string;
  previousUntil: number;
  remainingMinutes: number;
  source: string;
}

export function ManageClient() {
  const { clientId } = useParams<{ clientId: string }>();
  const { startImpersonation } = useImpersonation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showExport, setShowExport] = useState(false);

  const [client, setClient] = useState<Record<string, unknown> | null>(null);
  const [clientName, setClientName] = useState('');
  const [leadCount, setLeadCount] = useState(0);
  const [resultCount, setResultCount] = useState(0);
  const [recentResults, setRecentResults] = useState<Array<Record<string, unknown>>>([]);

  // Editable settings
  const [autoDispatch, setAutoDispatch] = useState(false);
  const [accreditedRequired, setAccreditedRequired] = useState(false);
  const [sheetId, setSheetId] = useState('');
  const [recallNoAnswer, setRecallNoAnswer] = useState(5);
  const [recallAnswered, setRecallAnswered] = useState(30);
  const [windowStart, setWindowStart] = useState('09:00');
  const [windowEnd, setWindowEnd] = useState('17:00');
  const [maxPerRun, setMaxPerRun] = useState(2000);
  const [billingType, setBillingType] = useState<'per_call' | 'per_contact'>('per_call');
  const [platformDaysRemaining, setPlatformDaysRemaining] = useState(0);
  const [platformAccessDays, setPlatformAccessDays] = useState('');

  // Monthly spend limits (editable by admin)
  const [monthlyCallLimit, setMonthlyCallLimit] = useState(30000);
  const [monthlyContactLimit, setMonthlyContactLimit] = useState(1200);
  const [savingLimits, setSavingLimits] = useState(false);
  const [limitsResult, setLimitsResult] = useState<string | null>(null);

  // Support chat feature flag
  const [supportChatEnabled, setSupportChatEnabled] = useState(false);
  const [schedulingEnabled, setSchedulingEnabled] = useState(true);

  // Inbound calling — admin force-disable + read-only view of user toggle.
  const [inboundEnabled, setInboundEnabled] = useState(false);
  const [inboundAdminOverride, setInboundAdminOverride] = useState(false);
  const [inboundAgentIdView, setInboundAgentIdView] = useState('');
  const [inboundOverrideSaving, setInboundOverrideSaving] = useState(false);

  // Send cool-off (written by the dispatcher after every manual send) + the
  // admin override that clears it. Purely derived from the client doc — the
  // card states no formula, so it can never contradict the dispatcher.
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [lastSendAt, setLastSendAt] = useState(0);
  const [lastSendCount, setLastSendCount] = useState(0);
  const [cooldownOverride, setCooldownOverride] = useState<CooldownOverrideInfo | null>(null);
  const [overridingCooldown, setOverridingCooldown] = useState(false);
  const [cooldownOverrideResult, setCooldownOverrideResult] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    if (!clientId) return;

    const unsubscribe = onSnapshot(doc(db, 'clients', clientId), snap => {
      if (snap.exists()) {
        const data = snap.data();
        setClient(data);
        setClientName((data.name as string) || '');
        setAutoDispatch(data.autoDispatchEnabled || false);
        setSheetId((data.sheetId as string) || '');
        const s = data.settings || {};
        setAccreditedRequired((s as Record<string, boolean>).accreditedRequired || false);
        setRecallNoAnswer((s as Record<string, number>).recallDaysNoAnswer || 5);
        setRecallAnswered((s as Record<string, number>).recallDaysAnswered || 30);
        setWindowStart((s as Record<string, string>).callingWindowStart || '09:00');
        setWindowEnd((s as Record<string, string>).callingWindowEnd || '17:00');
        setMaxPerRun((s as Record<string, number>).maxCallsPerRun || 2000);
        setBillingType((data.billingType as 'per_call' | 'per_contact') || 'per_call');
        setMonthlyCallLimit((data.monthlyCallLimit as number) || 30000);
        setMonthlyContactLimit((data.monthlyContactLimit as number) || 1200);
        setSupportChatEnabled(Boolean(data.supportChatEnabled));
        // Default to true so clients created before the schedulingEnabled
        // field existed continue to have access by default. Admin can flip
        // off explicitly below.
        setSchedulingEnabled(data.schedulingEnabled !== false);
        setInboundEnabled(Boolean(data.inboundEnabled));
        setInboundAdminOverride(Boolean(data.inboundAdminOverride));
        setInboundAgentIdView(typeof data.inboundAgentId === 'string' ? data.inboundAgentId : '');
        // Cool-off fields. The dispatcher writes plain ms-epoch numbers; the
        // override callable deletes cooldownUntil and writes cooldownOverride.
        setCooldownUntil(Number(data.cooldownUntil) || 0);
        setLastSendAt(Number(data.lastSendAt) || 0);
        setLastSendCount(Number(data.lastSendCount) || 0);
        const ov = data.cooldownOverride as Record<string, unknown> | undefined;
        if (ov && typeof ov === 'object') {
          const at = ov.at as Timestamp | undefined;
          setCooldownOverride({
            at: at && typeof at.toDate === 'function' ? at.toDate() : null,
            by: typeof ov.by === 'string' ? ov.by : '',
            previousUntil: Number(ov.previousUntil) || 0,
            remainingMinutes: Number(ov.remainingMinutes) || 0,
            source: typeof ov.source === 'string' ? ov.source : '',
          });
        } else {
          setCooldownOverride(null);
        }
        const ppu = data.platformPaidUntil as Timestamp | null;
        if (ppu && typeof ppu.toDate === 'function') {
          const days = Math.ceil((ppu.toDate().getTime() - Date.now()) / (24 * 60 * 60 * 1000));
          setPlatformDaysRemaining(Math.max(0, days));
        } else {
          setPlatformDaysRemaining(0);
        }
      }
    });

    // Fetch counts
    const fetchCounts = async () => {
      const leads = await getDocs(collection(db, 'clients', clientId, 'leads'));
      setLeadCount(leads.size);

      const results = await getDocs(collection(db, 'clients', clientId, 'results'));
      setResultCount(results.size);

      const recent = await getDocs(
        query(collection(db, 'clients', clientId, 'results'), orderBy('date', 'desc'), limit(5))
      );
      setRecentResults(recent.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    fetchCounts();

    return () => unsubscribe();
  }, [clientId]);

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!clientId) return;
    setSaving(true);
    setResult(null);

    try {
      const fn = httpsCallable(functions, 'updateClient');
      const updates: Record<string, unknown> = {
        sheetId: sheetId || undefined,
        autoDispatchEnabled: autoDispatch,
        billingType,
        settings: {
          recallDaysNoAnswer: recallNoAnswer,
          recallDaysAnswered: recallAnswered,
          callingWindowStart: windowStart,
          callingWindowEnd: windowEnd,
          maxCallsPerRun: maxPerRun,
          accreditedRequired: accreditedRequired,
        },
      };
      if (platformAccessDays !== '') {
        updates.platformAccessDays = parseInt(platformAccessDays) || 0;
      }
      await fn({ clientId, updates });
      setPlatformAccessDays('');
      setResult('Settings saved.');
    } catch (err) {
      setResult('Error: ' + (err instanceof Error ? err.message : 'Failed'));
    } finally {
      setSaving(false);
    }
  };

  const toggleSupportChat = async () => {
    if (!clientId) return;
    const newVal = !supportChatEnabled;
    try {
      await updateDoc(doc(db, 'clients', clientId), { supportChatEnabled: newVal });
      setSupportChatEnabled(newVal);
    } catch (err) {
      alert('Failed to update: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const toggleScheduling = async () => {
    if (!clientId) return;
    const newVal = !schedulingEnabled;
    try {
      await updateDoc(doc(db, 'clients', clientId), { schedulingEnabled: newVal });
      setSchedulingEnabled(newVal);
    } catch (err) {
      alert('Failed to update: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  // Force-disable inbound for this client. Goes through the callable rather
  // than direct updateDoc because flipping override→true ALSO unbinds the
  // inbound routing on Retell phones (so an in-flight callback during the
  // toggle window is rejected, not answered). Direct updateDoc would update
  // Firestore but leave Retell pointing at the inbound agent until a separate
  // sync ran.
  const toggleInboundOverride = async () => {
    if (!clientId) return;
    const newVal = !inboundAdminOverride;
    setInboundOverrideSaving(true);
    try {
      const fn = httpsCallable(functions, 'setInboundAdminOverride');
      await fn({ clientId, override: newVal });
      setInboundAdminOverride(newVal);
    } catch (err) {
      alert('Failed to update inbound override: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setInboundOverrideSaving(false);
    }
  };

  // Admin cool-off override: clears clients/{id}.cooldownUntil through the
  // adminClearCooldown callable (transaction + adminAuditLog row server-side).
  // One-shot — the client's next manual send starts a fresh cool-off as
  // normal. The onSnapshot above reflects the change. No confirm dialog, same
  // as the impersonation button on the dashboard (admin-only, reversible: the
  // previous value is kept in cooldownOverride.previousUntil + the audit row).
  const handleOverrideCooldown = async () => {
    if (!clientId) return;
    setOverridingCooldown(true);
    setCooldownOverrideResult(null);
    try {
      const fn = httpsCallable(functions, 'adminClearCooldown');
      // expectedUntil = the value this card is showing. If a send finished in
      // between, the server refuses (`cooldown_changed`) and writes nothing;
      // the snapshot then shows the new countdown. Local state is reset ONLY
      // on success (the transaction committed the delete, so it is truthful
      // now); on every no-op branch the snapshot stays the only writer.
      const res = await fn({ clientId, source: 'manage_client', expectedUntil: cooldownUntil });
      const data = res.data as { changed: boolean; reason?: string; remainingMinutesCleared?: number };
      if (data.changed) {
        setCooldownOverrideResult(`Cool-off cleared (had ${data.remainingMinutesCleared ?? 0} min left). The client can send now.`);
        setCooldownUntil(0);
      } else if (data.reason === 'cooldown_changed') {
        setCooldownOverrideResult('Cool-off changed since you looked (a send just finished). Click again to clear the new one.');
      } else {
        setCooldownOverrideResult('No active cool-off to clear.');
      }
      setTimeout(() => setCooldownOverrideResult(null), 6000);
    } catch (err) {
      setCooldownOverrideResult('Error: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setOverridingCooldown(false);
    }
  };

  const handleSaveLimits = async (e: FormEvent) => {
    e.preventDefault();
    if (!clientId) return;
    setSavingLimits(true);
    setLimitsResult(null);
    try {
      const fn = httpsCallable(functions, 'setClientLimits');
      await fn({
        clientId,
        monthlyCallLimit,
        monthlyContactLimit,
      });
      setLimitsResult('Monthly limits updated');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setLimitsResult('Error: ' + msg);
    } finally {
      setSavingLimits(false);
    }
  };

  if (!client) return <div className="loading">Loading...</div>;

  return (
    <div>
      <PortalHeader showCredits={false} />

      <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '16px', paddingBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Link to="/admin" className="back-link">Back</Link>
          <h1 style={{ margin: 0, fontSize: '1.2rem' }}>{(client.name as string) || 'Client'}</h1>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => {
              if (clientId) {
                startImpersonation(clientId, clientName || 'Client');
                navigate('/');
              }
            }}
            className="btn btn-primary"
            style={{ padding: '8px 16px', background: 'linear-gradient(90deg, #b8860b, #ddb154)', color: '#000', fontWeight: 600 }}
          >
            Enter as Client
          </button>
          {/* Hidden on the rehearsal bench: adding credits emails the client. */}
          {!IS_REHEARSAL && <Link to={`/admin/credits/${clientId}`} className="btn btn-primary" style={{ padding: '8px 16px' }}>
            Manage Credits
          </Link>}
          <button
            onClick={() => setShowExport(true)}
            className="btn btn-secondary"
            style={{ padding: '8px 16px' }}
          >
            Export Calls
          </button>
        </div>
      </div>

      <ExportEmailModal
        isOpen={showExport}
        onClose={() => setShowExport(false)}
        exportType="all"
        exportLabel="All Calls"
        clientId={clientId}
        defaultEmail={user?.email || 'info@jadenterprises.org'}
      />

      <div className="container">
        {/* Overview Stats */}
        <div className="stats-grid">
          <div className="stat-card green">
            <h3>{billingType === 'per_contact' ? 'Contact Credits' : 'Call Credits'}</h3>
            <span className="count">{billingType === 'per_contact' ? ((client.contactCredits as number) || 0) : ((client.credits as number) || 0)}</span>
          </div>
          <div className="stat-card blue">
            <h3>Total Leads</h3>
            <span className="count">{leadCount}</span>
          </div>
          <div className="stat-card orange">
            <h3>Total Results</h3>
            <span className="count">{resultCount}</span>
          </div>
          <div className="stat-card red">
            <h3>Email</h3>
            <span style={{ fontSize: '0.9rem', color: 'var(--gray-200)' }}>{(client.email as string) || ''}</span>
          </div>
        </div>

        {/* Settings Form */}
        <h2 className="section-title">Client Settings</h2>
        <form onSubmit={handleSave} style={{ marginBottom: '32px' }}>
          <div className="form-group" style={{ marginBottom: '16px' }}>
            <label>Google Sheet ID (results sync to 4 tabs automatically)</label>
            <input type="text" value={sheetId} onChange={e => setSheetId(e.target.value)}
              placeholder="e.g. 1s4VSkOy1EF-AqC06xbLJZLjGk6n8zIyfiEKAgtP02M4"
              style={{ fontFamily: 'monospace', fontSize: '0.85rem' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div className="form-group">
              <label>Billing Type</label>
              <select value={billingType} onChange={e => setBillingType(e.target.value as 'per_call' | 'per_contact')}>
                <option value="per_call">Per Call — $0.15/call</option>
                <option value="per_contact">Per Contact — $3.50/contact</option>
              </select>
            </div>
            <div className="form-group">
              <label>Platform Access Days {platformDaysRemaining > 0 ? <span style={{ color: 'var(--green)', fontWeight: 400, fontSize: '0.8rem' }}>({platformDaysRemaining}d remaining)</span> : <span style={{ color: 'var(--red)', fontWeight: 400, fontSize: '0.8rem' }}>(none)</span>}</label>
              <input type="number" value={platformAccessDays} onChange={e => setPlatformAccessDays(e.target.value)}
                placeholder="Leave blank to keep current" min="0" max="3650" />
            </div>
            <div className="form-group">
              <label>Auto Dispatch</label>
              <select value={autoDispatch ? 'true' : 'false'} onChange={e => setAutoDispatch(e.target.value === 'true')}>
                <option value="false">Disabled</option>
                <option value="true">Enabled</option>
              </select>
            </div>
            <div className="form-group">
              <label>Accredited Required</label>
              <select value={accreditedRequired ? 'true' : 'false'} onChange={e => setAccreditedRequired(e.target.value === 'true')}>
                <option value="false">No — non-accredited pass through</option>
                <option value="true">Yes — non-accredited closed</option>
              </select>
            </div>
            <div className="form-group">
              <label>Recall Days (No Answer)</label>
              <input type="number" value={recallNoAnswer} onChange={e => setRecallNoAnswer(parseInt(e.target.value) || 5)} min="1" max="90" />
            </div>
            <div className="form-group">
              <label>Recall Days (Answered)</label>
              <input type="number" value={recallAnswered} onChange={e => setRecallAnswered(parseInt(e.target.value) || 30)} min="1" max="180" />
            </div>
            <div className="form-group">
              <label>Window Start</label>
              <input type="time" value={windowStart} onChange={e => setWindowStart(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Window End</label>
              <input type="time" value={windowEnd} onChange={e => setWindowEnd(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Max Calls Per Run (1–5000)</label>
              <input type="number" value={maxPerRun} onChange={e => setMaxPerRun(parseInt(e.target.value) || 2000)} min="1" max="5000" />
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>

          {result && (
            <span style={{ marginLeft: '12px', color: result.startsWith('Error') ? 'var(--red)' : 'var(--green)' }}>
              {result}
            </span>
          )}
        </form>

        {/* Monthly Spend Limits — separate form so admin can raise caps without touching other settings */}
        <h2 className="section-title">Monthly Spend Limits</h2>
        <p style={{ color: 'var(--gray-400)', marginBottom: '12px', fontSize: '0.9rem' }}>
          Defaults: 30,000 calls / $3,900 &middot; 1,200 contacts / $3,960. Raise these when a client
          submits a limit-raise request or you&rsquo;ve verified them as established.
        </p>
        <form onSubmit={handleSaveLimits} style={{ marginBottom: '32px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '16px' }}>
            <div className="form-group">
              <label>Monthly Call Limit (per_call clients)</label>
              <input
                type="number"
                value={monthlyCallLimit}
                onChange={e => setMonthlyCallLimit(parseInt(e.target.value) || 0)}
                min="0"
                max="10000000"
              />
              <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginTop: '4px' }}>
                ~${(monthlyCallLimit * 0.13).toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo at $0.13/call
              </div>
            </div>
            <div className="form-group">
              <label>Monthly Contact Limit (per_contact clients)</label>
              <input
                type="number"
                value={monthlyContactLimit}
                onChange={e => setMonthlyContactLimit(parseInt(e.target.value) || 0)}
                min="0"
                max="1000000"
              />
              <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginTop: '4px' }}>
                ~${(monthlyContactLimit * 3.30).toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo at $3.30/contact
              </div>
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={savingLimits}>
            {savingLimits ? 'Saving...' : 'Save Limits'}
          </button>
          {limitsResult && (
            <span style={{ marginLeft: '12px', color: limitsResult.startsWith('Error') ? 'var(--red)' : 'var(--green)' }}>
              {limitsResult}
            </span>
          )}
        </form>

        {/* Standing discount for this client (credit packs % + platform fee %) */}
        {/* key = a fresh card per client, so typed-but-unsaved values can never follow you to another client */}
        {clientId && <ClientDiscountCard key={clientId} clientId={clientId} />}
        {clientId && <ClientPromoCodesCard key={`promo-${clientId}`} clientId={clientId} />}

        {/* Send Cool-Off — admin override */}
        <section style={{ marginBottom: '32px', padding: '16px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}>
          <h3 style={{ marginTop: 0 }}>Send Cool-Off (manual sends)</h3>
          <p style={{ opacity: 0.7, marginTop: '4px', marginBottom: '8px', fontSize: '0.9rem' }}>
            Status: <CooldownRemaining cooldownUntil={cooldownUntil} onExpired={() => setCooldownUntil(0)} />
            {lastSendAt > 0 && (
              <span> &middot; last send {lastSendCount.toLocaleString()} leads at {formatWhen(lastSendAt)}</span>
            )}
          </p>
          {cooldownOverride && cooldownOverride.at && (
            <p style={{ opacity: 0.7, marginTop: 0, marginBottom: '8px', fontSize: '0.85rem' }}>
              Last override: cleared by admin at {formatWhen(cooldownOverride.at.getTime())} (had {cooldownOverride.remainingMinutes} min left
              {cooldownOverride.source ? `, from ${cooldownOverride.source.replace('_', ' ')}` : ''}).
            </p>
          )}
          <p style={{ opacity: 0.7, marginTop: 0, marginBottom: '12px', fontSize: '0.85rem' }}>
            After every manual send the dispatcher blocks the next one for a while so results can process.
            Clearing it lets this client (or you, via &ldquo;Enter as Client&rdquo;) send more leads right away.
            One-shot: the next send starts a new cool-off as normal. Every clear is written to the admin audit log.
          </p>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleOverrideCooldown}
            disabled={overridingCooldown || cooldownUntil <= Date.now()}
            style={{ borderColor: 'var(--gold-dark)', color: 'var(--gold)' }}
          >
            {overridingCooldown ? 'Clearing…' : 'Override cool-off — clear now'}
          </button>
          {cooldownOverrideResult && (
            <span style={{ marginLeft: '12px', color: cooldownOverrideResult.startsWith('Error') ? 'var(--red)' : 'var(--green)' }}>
              {cooldownOverrideResult}
            </span>
          )}
        </section>

        {/* Scheduled Calls toggle */}
        <section style={{ marginBottom: '32px', padding: '16px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}>
          <h3 style={{ marginTop: 0 }}>Scheduled Calls</h3>
          <p style={{ opacity: 0.7, marginTop: '4px', marginBottom: '12px', fontSize: '0.9rem' }}>
            When enabled, this client can create one-time and weekly scheduled call dispatches
            from their dashboard (cap: 3 per day). Disable to revoke access — existing schedules
            stay in place but the client can't create new ones until re-enabled.
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={schedulingEnabled}
              onChange={toggleScheduling}
            />
            Scheduled Calls Enabled for this Client
          </label>
        </section>

        {/* Support Chat Assistant toggle */}
        <section style={{ marginBottom: '32px', padding: '16px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}>
          <h3 style={{ marginTop: 0 }}>Support Chat Assistant</h3>
          <p style={{ opacity: 0.7, marginTop: '4px', marginBottom: '12px', fontSize: '0.9rem' }}>
            When enabled, this client can access the AI support chat assistant from the Help
            Center page. Diagnostic tools read their account data and escalate to your inbox
            when needed.
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={supportChatEnabled}
              onChange={toggleSupportChat}
            />
            Support Chat Enabled for this Client
          </label>
        </section>

        {/* Inbound Calling — admin override */}
        <section style={{ marginBottom: '32px', padding: '16px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}>
          <h3 style={{ marginTop: 0 }}>Inbound Calling</h3>
          <p style={{ opacity: 0.7, marginTop: '4px', marginBottom: '12px', fontSize: '0.9rem' }}>
            Inbound agent: <strong>{inboundAgentIdView || '(none assigned)'}</strong> ·
            User toggle: <strong>{inboundEnabled ? 'ON' : 'OFF'}</strong>
            {inboundAdminOverride && (
              <span style={{ color: 'var(--red)', marginLeft: '6px' }}>(force-disabled by admin)</span>
            )}
          </p>
          <p style={{ opacity: 0.7, marginTop: 0, marginBottom: '12px', fontSize: '0.85rem' }}>
            Force-disable inbound calling for this client regardless of their dashboard toggle.
            Flipping ON immediately unbinds inbound routing from their outbound phones so callbacks
            in flight are rejected at the Retell edge. Use sparingly — for billing disputes,
            abuse, or compliance holds.
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={inboundAdminOverride}
              onChange={toggleInboundOverride}
              disabled={inboundOverrideSaving}
            />
            Force-Disable Inbound Calling for this Client
          </label>
        </section>

        {/* Recent Results */}
        <h2 className="section-title">Recent Results</h2>
        {recentResults.length === 0 ? (
          <div className="loading">No results yet</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Classification</th>
                  <th>Quality</th>
                </tr>
              </thead>
              <tbody>
                {recentResults.map(r => (
                  <tr key={r.id as string}>
                    <td>{(r.date as { toDate: () => Date })?.toDate?.()?.toLocaleDateString?.() || ''}</td>
                    <td>{`${r.firstName || ''} ${r.lastName || ''}`.trim() || 'Unknown'}</td>
                    <td>{r.phone as string}</td>
                    <td>{r.classification as string}</td>
                    <td>{r.quality as string}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Reset Client Password */}
        <div style={{ marginTop: '32px', padding: '24px', border: '1px solid var(--dark-3)', borderRadius: '10px', background: 'var(--dark-2)' }}>
          <h2 className="section-title" style={{ marginBottom: '12px' }}>Reset Client Password</h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label>New Password (min 8 characters)</label>
              <input type="text" id="resetPw" placeholder="Enter new password" minLength={8}
                style={{ fontFamily: 'monospace' }} />
            </div>
            <button className="btn btn-primary" style={{ padding: '8px 20px', whiteSpace: 'nowrap' }}
              disabled={IS_REHEARSAL} title={IS_REHEARSAL ? 'Switched off on the rehearsal bench' : undefined}
              onClick={async () => {
                const pw = (document.getElementById('resetPw') as HTMLInputElement)?.value;
                if (!pw || pw.length < 8) { alert('Password must be at least 8 characters'); return; }
                try {
                  const fn = httpsCallable(functions, 'resetClientPassword');
                  await fn({ clientId, newPassword: pw });
                  alert('Password reset successfully.');
                  (document.getElementById('resetPw') as HTMLInputElement).value = '';
                } catch (err) {
                  alert('Failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
                }
              }}>
              Reset Password
            </button>
          </div>
        </div>

        {/* Danger Zone — Delete Client */}
        <div style={{ marginTop: '24px', padding: '24px', border: '1px solid var(--red)', borderRadius: '10px', background: 'rgba(239,68,68,0.05)' }}>
          <h2 style={{ color: 'var(--red)', fontSize: '1.1rem', fontWeight: 600, marginBottom: '12px' }}>Danger Zone</h2>
          <p style={{ color: 'var(--gray-400)', fontSize: '0.9rem', marginBottom: '16px' }}>
            Permanently delete this client, all their leads, results, credit history, agent assignments, and webhook routing.
            This cannot be undone.
          </p>
          <button
            className="btn"
            style={{ background: 'var(--red-bg)', color: 'var(--red)', border: '1px solid var(--red)' }}
            onClick={async () => {
              const typed = prompt(`Type the client name "${clientName}" to confirm deletion:`);
              if (!typed || typed !== clientName) {
                alert('Name did not match. Deletion cancelled.');
                return;
              }
              if (!confirm(`FINAL WARNING: Delete "${clientName}" and ALL their data permanently?`)) return;

              try {
                const fn = httpsCallable(functions, 'deleteClient');
                const res = await fn({ clientId, confirmName: clientName });
                const data = res.data as { message: string; details: string[] };
                alert(`${data.message}\n\n${data.details.join('\n')}`);
                navigate('/admin');
              } catch (err) {
                alert('Delete failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
              }
            }}
          >
            Delete Client Permanently
          </button>
        </div>
      </div>
    </div>
  );
}
